import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { client } from './client';
import type { Thread, Message, ThreadParticipant, Proposal } from '../types';

export type ThreadStatus = 'active' | 'waiting' | 'resolved' | 'archived';

interface ThreadsParams {
  status?: 'active' | 'waiting' | 'resolved';
  mailboxId?: string;
  limit?: number;
  cursor?: string;
}

export interface ThreadWithMessages extends Thread {
  messages: Message[];
  participants: ThreadParticipant[];
}

interface ThreadsResponse {
  threads: Thread[];
}

interface ThreadResponse {
  thread: Thread;
  messages: Message[];
  participants: ThreadParticipant[];
}

export function useThreads(params: ThreadsParams = {}): UseQueryResult<Thread[], Error> {
  return useQuery({
    queryKey: ['threads', params],
    queryFn: async () => {
      const response = await client.get<ThreadsResponse>('/threads', { params });
      return response.data.threads;
    },
    staleTime: 0, // Always consider stale - ignore global 5min staleTime
    refetchInterval: 1000, // 1 second for quick updates
  });
}

export function useThread(threadId: string | null): UseQueryResult<ThreadWithMessages, Error> {
  return useQuery({
    queryKey: ['thread', threadId],
    queryFn: async () => {
      const response = await client.get<ThreadResponse>(`/threads/${threadId}`);
      // Combine thread, messages and participants into ThreadWithMessages
      return {
        ...response.data.thread,
        messages: response.data.messages,
        participants: response.data.participants,
      };
    },
    enabled: !!threadId,
    staleTime: 0, // Always consider stale - ignore global 5min staleTime
    refetchInterval: 1000, // 1 second for quick updates
  });
}

export function useUpdateThreadStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, status }: { threadId: string; status: ThreadStatus }) =>
      client.patch(`/threads/${threadId}/status`, { status }),
    onSuccess: (_, { threadId }) => {
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

// ============================================================================
// Send Reply Hook
// ============================================================================

interface SendReplyParams {
  threadId: string;
  text: string;
  sendRequestId: string;
  replyToMessageId?: string;
}

interface SendReplyResponse {
  messageId: string;
  status: 'sending' | 'sent' | 'failed';
}

/**
 * Hook for sending email replies.
 * Uses sendRequestId for idempotency.
 */
export function useSendReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId, text, sendRequestId, replyToMessageId }: SendReplyParams) => {
      const response = await client.post<SendReplyResponse>(`/threads/${threadId}/reply`, {
        text,
        sendRequestId,
        replyToMessageId,
      });
      return response.data;
    },
    onSuccess: (_, { threadId }) => {
      // Invalidate thread to show new message
      queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['threads'] });
    },
  });
}

// ============================================================================
// Retry Failed Message
// ============================================================================

export function useRetryMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (messageId: string) => {
      const { data } = await client.post<{ success: boolean; messageId: string }>(
        `/messages/${messageId}/retry`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thread'] });
      queryClient.invalidateQueries({ queryKey: ['threads'] });
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

// ============================================================================
// Thread Proposals Hook (Phase 3)
// ============================================================================

interface ProposalsResponse {
  proposals: Proposal[];
}

/**
 * Hook for fetching proposals for a thread.
 */
export function useThreadProposals(
  threadId: string | null,
  options: { limit?: number } = {}
): UseQueryResult<Proposal[], Error> {
  const { limit = 50 } = options;

  return useQuery({
    queryKey: ['thread', threadId, 'proposals', limit],
    queryFn: async () => {
      const response = await client.get<ProposalsResponse>(
        `/threads/${threadId}/proposals`,
        { params: { limit } }
      );
      return response.data.proposals;
    },
    enabled: !!threadId,
    staleTime: 0, // Always consider stale - ignore global 5min staleTime
    refetchInterval: 2000, // 2 seconds for proposals (async generation)
  });
}
